import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getCapacityInfo,
  getIntegrationAvailabilities,
} from '../../../common/api/capacityService';

import {
  fetchBookingContainerDetails,
  getbookingsbyaccommodation,
} from '../../../common/api/bookingsService';
import { fetchSeasonRatesByAccommodationId } from '../../../common/api/seasonRatesService';
import { fetchAccommodationDetails } from '../../../common/api/accommodationsService';
import {
  fetchavailabilitiesforsingleaccommodation,
  isAvailable,
  maketempbooking,
  pollJobStatus,
} from '../../../common/api/sportumService';

export interface CalendarReservedDate {
  ReservedDate: Date;
  Type: 'a' | 'i' | 'o' | 'n' | 'r' | 'v' | 'y';
}

export interface CalendarReservedDates {
  ReservedDates: Date[];
  Type: 'a' | 'i' | 'o' | 'n' | 'r' | 'v' | 'y';
}

export interface MaxCapacityInfo {
  MaxCapacity: number;
}

export interface GetBookingsByAccommodationRequest {
  AccommodationId: string;
  BookingsStartDate: Date;
  BookingsEndDate: Date;
  IgnoreBookings: boolean;
}

interface CheckAvailabilityPayload {
  accommodationId: string;
  start: Date;
  end: Date;
}

export const GetMaxCapacityInfoAsync = createAsyncThunk(
  'capacity/getCapacityInfo',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response = await getCapacityInfo(accommodationId);
      // console.log('max data:', response.result);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.result);
    }
  },
);

export const GetIntegrationAvailabilitiesAsync = createAsyncThunk(
  'capacity/getIntegrationAvailabilities',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response = await getIntegrationAvailabilities(
        accommodationId,
        1,
        '00000000-0000-0000-0000-000000000000',
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.result);
    }
  },
);

export const GetBookingsByAccommodation = createAsyncThunk(
  'bookings/getBookingsByAccommodation',
  async (request: GetBookingsByAccommodationRequest, { rejectWithValue }) => {
    try {
      // eslint-disable-next-line no-debugger
      const response = await getbookingsbyaccommodation(request);
      // console.log('bookings', response);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.result);
    }
  },
);

export const GetBookingsByAccommodationIgnoreBookings = createAsyncThunk(
  'bookings/getBookingsByAccommodation',
  async (request: GetBookingsByAccommodationRequest, { rejectWithValue }) => {
    try {
      // eslint-disable-next-line no-debugger
      const response = await getbookingsbyaccommodation(request);
      // console.log('bookings', response);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.result);
    }
  },
);

export const getBookingContainerDetailsAsync = createAsyncThunk(
  'bookings/getBookingContainerDetailsAsync',
  async (bookingId: string, { rejectWithValue }) => {
    try {
      const response = await fetchBookingContainerDetails(bookingId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getSeasonRatesByAccommodationAsync = createAsyncThunk(
  'pricing/getSeasonRatesByAccommodation',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response = await fetchSeasonRatesByAccommodationId(accommodationId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchAccommodationDetailsAsync = createAsyncThunk(
  'accommodation/fetchAccommodationDetails',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response = await fetchAccommodationDetails(accommodationId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const isSportumCapacityAvailableAsync = createAsyncThunk(
  'sportum/isAvailable',
  async (
    { accommodationId, start, end }: CheckAvailabilityPayload, // Add type definition for the parameters
    { rejectWithValue },
  ) => {
    try {
      const response = await isAvailable(accommodationId, start, end); // Use the function with the destructured payload
      console.log('isSportumCapacityAvailableAsync', response);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message); // Handle error properly
    }
  },
);

export const makeSportumTempBookingAsync = createAsyncThunk(
  'integrations/sportum/MakeTempBooking',
  async (bookingId: string, { rejectWithValue }) => {
    try {
      return await maketempbooking(bookingId);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchAvailabilitiesForSportumAccommodationAsync = createAsyncThunk(
  'integrations/sportum/FetchAvailabilitiesForSingleAccommodation',
  async (accommodationId: string, { rejectWithValue }) => {
    let startResponse;

    try {
      startResponse =
        await fetchavailabilitiesforsingleaccommodation(accommodationId);
    } catch (error: unknown) {
      if (
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        (error as { response?: { status?: number } }).response?.status === 404
      ) {
        return {
          jobStatus: {
            status: 'completed',
            hangfireState: 'Succeeded',
          },
        };
      }

      return rejectWithValue(
        (error as { response?: { data?: unknown }; message?: string })?.response
          ?.data || error,
      );
    }

    const jobId = startResponse?.jobId || startResponse?.JobId;

    if (!jobId) {
      return startResponse;
    }

    const delay = (ms: number) =>
      new Promise<void>(resolve => {
        setTimeout(resolve, ms);
      });

    const pollUntilDone = async (attempt: number): Promise<unknown> => {
      try {
        const statusResponse = await pollJobStatus(jobId);
        const status = (statusResponse?.status || statusResponse?.Status || '')
          .toString()
          .toLowerCase();

        if (
          status === 'completed' ||
          status === 'succeeded' ||
          status === 'done'
        ) {
          return {
            ...startResponse,
            jobStatus: statusResponse,
          };
        }

        if (status === 'failed' || status === 'error') {
          return rejectWithValue(statusResponse);
        }

        if (attempt >= 120) {
          const timeoutError = new Error(
            'Timed out while waiting for Sportum availability job.',
          );
          (timeoutError as Error & { jobId?: string }).jobId = jobId;
          return rejectWithValue(timeoutError);
        }

        await delay(5000);
        return pollUntilDone(attempt + 1);
      } catch (error: unknown) {
        if (
          typeof error === 'object' &&
          error !== null &&
          'response' in error
        ) {
          const axiosError = error as {
            response?: { status?: number; data?: unknown };
          };

          if (axiosError.response?.status === 404) {
            return {
              ...startResponse,
              jobStatus: {
                jobId,
                status: 'completed',
                hangfireState: 'Succeeded',
              },
            };
          }

          return rejectWithValue(axiosError.response?.data || error);
        }

        return rejectWithValue(error);
      }
    };

    return pollUntilDone(0);
  },
);
